import React, { useEffect, useRef } from 'react';
import { redirectGuard } from '../../lib/auth/redirectGuard';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';

// Import UI components
import {
  Button,
  CardTitle,
  CardDescription,
  CardContent,
  Loader2 // Import Loader2
} from "@pagespeedify/ui";

// Import global styles and component styles
import "@pagespeedify/ui/styles/globals.css";
import "../../assets/styles/login.css"; // Assuming this contains specific login styles if needed

export default function Login() {
  const { isAuthenticated, isLoading: isAuthLoading, signInWithGoogle, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isGoogleRedirect = searchParams.get('with') === 'google';
  const hasInitiatedRedirect = useRef(false);
  const bcRef = useRef<BroadcastChannel | null>(null);
  const LS_FALLBACK_KEY = 'swayashin_google_redirect_msg';
  const REDIRECT_KEY = 'google_redirect_initiated_at';
  // TTL can be configured via Vite env var VITE_GOOGLE_REDIRECT_TTL_MS (milliseconds)
  const REDIRECT_TTL_MS = (typeof import.meta !== 'undefined' && (import.meta as any).env && Number((import.meta as any).env.VITE_GOOGLE_REDIRECT_TTL_MS))
    ? Number((import.meta as any).env.VITE_GOOGLE_REDIRECT_TTL_MS)
    : 60 * 1000; // default 60 seconds
  const BC_NAME = 'swayashin_google_redirect';

  useEffect(() => {
    // Setup BroadcastChannel for cross-tab coordination if available
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        const bc = new BroadcastChannel(BC_NAME);
        bc.onmessage = (ev) => {
          try {
            const data = ev?.data;
            if (!data) return;
            if (data.type === 'initiated' && data.ts) {
              // Mirror into sessionStorage so this tab respects the initiation
              try { sessionStorage.setItem(REDIRECT_KEY, String(data.ts)); } catch (e) {}
              hasInitiatedRedirect.current = true;
            }
            if (data.type === 'cleared') {
              try { sessionStorage.removeItem(REDIRECT_KEY); } catch (e) {}
            }
          } catch (e) {
            console.warn('BroadcastChannel message handling error', e);
          }
        };
        bcRef.current = bc;
      } else if (typeof window !== 'undefined') {
        // Fallback: use localStorage 'storage' events to coordinate across tabs.
        const storageListener = (ev: StorageEvent) => {
          try {
            if (ev.key !== LS_FALLBACK_KEY) return;
            if (!ev.newValue) return;
            const payload = JSON.parse(ev.newValue);
            if (payload?.type === 'initiated' && payload?.ts) {
              try { sessionStorage.setItem(REDIRECT_KEY, String(payload.ts)); } catch (e) {}
              hasInitiatedRedirect.current = true;
            }
            if (payload?.type === 'cleared') {
              try { sessionStorage.removeItem(REDIRECT_KEY); } catch (e) {}
            }
          } catch (e) {
            console.warn('localStorage fallback message handling error', e);
          }
        };
        // store the listener so we can remove it on cleanup
        (bcRef as any).storageListener = storageListener;
        (window as any).addEventListener('storage', storageListener);
      }
    } catch (e) {
      console.warn('BroadcastChannel or localStorage fallback initialization failed', e);
    }

    // Mirror any existing localStorage fallback key into sessionStorage on mount.
    // This handles the case where the app did a full redirect to Google and
    // returned: the localStorage fallback survives across the navigation, but
    // sessionStorage does not. If we see the fallback message and it is recent,
    // restore the redirect guard into sessionStorage so the rest of the app
    // treats this as an intentional redirect flow.
    try {
      if (typeof window !== 'undefined' && 'localStorage' in window) {
        const raw = localStorage.getItem(LS_FALLBACK_KEY);
        if (raw) {
          try {
            const payload = JSON.parse(raw);
            if (payload?.type === 'initiated' && payload?.ts) {
              const now = Date.now();
              // If the fallback is within TTL, restore into sessionStorage
              if (now - Number(payload.ts) < REDIRECT_TTL_MS) {
                try { sessionStorage.setItem(REDIRECT_KEY, String(payload.ts)); } catch (e) {}
                hasInitiatedRedirect.current = true;
              }
            }
          } catch (e) {
            // ignore malformed JSON
          }
        }
      }
    } catch (e) {}

    // If the user is already authenticated, redirect them to the dashboard.
    if (!isAuthLoading && isAuthenticated) {
      console.log('Login Page: User is authenticated, checking for redirect.');
      try { sessionStorage.removeItem(REDIRECT_KEY); } catch (e) {}
      try { bcRef.current?.postMessage({ type: 'cleared' }); } catch (e) {}
      try {
        if (typeof window !== 'undefined' && !('BroadcastChannel'in window)) {
          localStorage.setItem(LS_FALLBACK_KEY, JSON.stringify({ type: 'cleared', ts: Date.now() }));
          setTimeout(() => { try { localStorage.removeItem(LS_FALLBACK_KEY); } catch(e){} }, 500);
        }
      } catch (e) {}

      const redirectUri = searchParams.get('redirect_uri');
      navigate('/', { replace: true, state: { redirectUri } });
      return;
    }

    // If this is the redirect from the landing page, initiate the Google sign-in.
    if (isGoogleRedirect && !isAuthLoading && !isAuthenticated && !hasInitiatedRedirect.current) {
      // Prevent duplicate initiations across remounts or strict-mode double-mounts by
      // checking a timestamped sessionStorage value. If the previous initiation is
      // recent (within REDIRECT_TTL_MS), skip re-initiating.
      try {
        if (redirectGuard.isRecent(REDIRECT_TTL_MS)) {
          console.log('Login Page: Detected recent google redirect initiation; skipping duplicate initiation.');
          hasInitiatedRedirect.current = true; // mark so we don't attempt again this mount
          return;
        }
      } catch (e) {
        console.warn('Login Page: failed to read redirect key from sessionStorage', e);
      }

      console.log('Login Page: Initiating Google sign-in with environment-appropriate method.');
      hasInitiatedRedirect.current = true; // Prevent re-triggering within this mount
      try {
  const now = Date.now();
  redirectGuard.setTimestamp(now);
        try { bcRef.current?.postMessage({ type: 'initiated', ts: now }); } catch (e) {}
        try {
          if (typeof window !== 'undefined' && !('BroadcastChannel' in window)) {
            localStorage.setItem(LS_FALLBACK_KEY, JSON.stringify({ type: 'initiated', ts: now }));
            setTimeout(() => { try { localStorage.removeItem(LS_FALLBACK_KEY); } catch(e){} }, 500);
          }
        } catch (e) {}
      } catch (e) {
        console.warn('Login Page: failed to set redirect key in sessionStorage', e);
      }

      // Use popup for localhost (better for development), redirect for production
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const preferredMethod = isLocalhost ? 'popup' : 'redirect';

      console.log(`Using ${preferredMethod} method for ${isLocalhost ? 'localhost' : 'production'} environment`);
      signInWithGoogle(preferredMethod);
    }

    return () => {
      try {
        if (bcRef.current) {
          bcRef.current.close();
          bcRef.current = null;
        }
      } catch (e) {}
      try {
        const storageListener = (bcRef as any).storageListener;
        if (storageListener) {
          (window as any).removeEventListener('storage', storageListener);
        }
      } catch (e) {}
    };
  }, [isAuthenticated, isAuthLoading, isGoogleRedirect, navigate, signInWithGoogle]);

  // Consolidated Sign In Handler for the button on the login page
  const handleSignIn = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    console.log("Starting Google sign-in process from button click");
    try {
      if (error) {
        clearError(); 
      }
      // Use the default popup method when the button is clicked directly
      const success = await signInWithGoogle();
      if (success) {
        console.log("Google sign-in process initiated successfully!");
      } else {
        console.log("Google sign-in failed or was cancelled by user.");
      }
    } catch (err) {
      console.error("Unhandled error during sign-in attempt:", err);
    }
  };

  // Current year for copyright
  const currentYear = new Date().getFullYear();

  // --- Loading State ---
  // Show styled loading indicator ONLY when isAuthLoading is true
  if (isAuthLoading) {
     console.log('Login Page: Rendering LOGIN LOADER TEXT state');
     return (
       <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#1a1f35] to-[#0f172a]">
         <Loader2 className="h-12 w-12 text-white animate-spin" />
       </div>
     );
  }

  // If this is a redirect from the landing page, show a simple loading state
  // instead of the full login form while we wait for the authentication to complete.
  if (isGoogleRedirect && !isAuthenticated) {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-[#1a1f35] to-[#0f172a] text-white">
        <Loader2 className="h-8 w-8 animate-spin mb-4" />
        <p className="text-lg">Connecting to Google...</p>
        <p className="text-sm text-gray-400 mt-2">
          {isLocalhost 
            ? "Opening popup window for sign-in" 
            : "Redirecting to Google for authentication"
          }
        </p>
      </div>
    );
  }

  // --- Authenticated State (but redirect hasn't happened yet) ---
  // If loading is done but user is authenticated, render null or minimal placeholder
  // while the useEffect redirects them. This prevents flashing the login form.
  if (!isAuthLoading && isAuthenticated) {
      console.log('Login Page: Rendering null while redirecting (isAuthenticated=true, isAuthLoading=false)');
      return null; // Or a minimal non-styled placeholder if null causes issues
  }

  // --- Render Login Form ---
  // This part only renders if isAuthLoading is false AND isAuthenticated is false
  console.log('Login Page: Rendering login form (isAuthLoading=false, isAuthenticated=false)');
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#1a1f35] to-[#0f172a]">
      <div className="w-full max-w-[420px] login-container">
        <div className="login-card relative bg-slate-900/70 backdrop-blur-md rounded-none shadow-xl border border-white/10 overflow-hidden">
          <div className="p-10 flex flex-col gap-6">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <svg className="w-16 h-16" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="14" fill="#4285f4"></circle>
                  <path d="M16,8 L10,18 L16,18 L16,24 L22,14 L16,14 Z" fill="white"></path>
                </svg>
              </div>
              <CardTitle className="text-2xl text-white font-semibold">Swayashin</CardTitle>
              <CardDescription className="text-gray-400 mt-1">Sign in to access your account</CardDescription>
            </div>
            
            <CardContent className="p-0 pt-4">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-md text-center text-red-400 text-sm mb-4">
                  {error.message || 'An unknown error occurred.'} 
                </div>
              )}
              
              <Button
                onClick={handleSignIn}
                variant="outline"
                className="w-full h-11 text-base text-gray-200 px-4
                         bg-gray-800 border-gray-700
                         hover:bg-gray-700 hover:text-white flex items-center justify-center" // Added flex for centering button content
              >
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 24 24" className="mr-2">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    <path d="M1 1h22v22H1z" fill="none" />
                  </svg>
                  Sign in with Google
                </>
              </Button>
            </CardContent>
          </div>
        </div>
        
        <div className="mt-8 text-center text-gray-400 text-xs">
          <p>© {currentYear} Swayashin. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}